import type { ExportNotification } from '../actions/export';
import type { CollectionSelector } from '../common/collection.selector';
import type { JobResult } from '../actions/collection.import.excel';
import type { LongRunningExportType, VariantExportSelection } from '../actions/export';

export const LongRunningTaskStatus = {
	RUNNING: 'running',
	COMPLETED: 'completed',
	COMPLETED_WARNINGS: 'completed-warnings',
	COMPLETED_ERRORS: 'completed-errors',
	FAILED: 'failed',
} as const;

export type LongRunningTaskStatus = (typeof LongRunningTaskStatus)[keyof typeof LongRunningTaskStatus];

export const LongRunningTaskType = {
	EXPORT: 'export',
	IMPORT: 'import',
	DUPLICATE: 'duplicate',
} as const;

export type LongRunningTaskType = (typeof LongRunningTaskType)[keyof typeof LongRunningTaskType];

/** File info for a completed export task (no notifications — those live at the response/document level) */
export interface ExportTaskResult {
	exportId: string;
	fileName: string;
	url: string;
}

export interface ExportTaskPayload {
	/** The type of long-running export */
	exportType: LongRunningExportType;
	/** Flat array of collection revision strings */
	revisions: string[];
	/** Client's timezone (e.g. 'Pacific/Auckland') for filename dates and content formatting */
	timezone: string;
	/** Whether to exclude photos from the export */
	noPhotos?: boolean;
	/** Checked "Include variants" checkboxes; ignored by export types that don't support variant filtering */
	variantGroups?: VariantExportSelection[];
}

/** Input data for a long-running import task */
export interface ImportTaskPayload {
	/** Attachment ID of the uploaded Excel or ZIP file */
	attachmentId: string;
	/** Tag name to import collections into (null when importing from a ZIP, where names come from filenames) */
	tagName: string | null;
	/** Form revision to use; if omitted it is read from the Excel file metadata */
	formRevision?: string;
}

/** Result data for a completed import task */
export interface ImportTaskResult {
	jobResults: JobResult[];
}

/** Input data for a long-running duplicate task */
export interface DuplicateTaskPayload {
	/** Source collections to duplicate (resolved server-side via CollectionFetcher) */
	collections: CollectionSelector[];
	/** Target tag name (created if absent) */
	tagName: string;
	/** Optional cross-department target — when omitted, duplicates within the request's department */
	toDepartmentId?: string;
}

/**
 * Result data for a completed duplicate task.
 *
 * Note: a duplicate task may return a populated `result` even on `COMPLETED_ERRORS` —
 * partial successes are still surfaced to the client so the user can see which copies
 * were created. This diverges from `ExportTaskResult`, which is only present on
 * `COMPLETED` / `COMPLETED_WARNINGS`.
 */
export interface DuplicateTaskResult {
	tagId: string;
	count: number;
	collections: { id: string; uuid: string; revision: string }[];
}

export type DuplicateNotificationCode =
	| 'duplicate_failed'
	| 'span_demoted_no_partner'
	| 'span_demoted_partner_failed'
	| 'span_demoted_no_location';

/**
 * Per-vector-field entry describing a demoted span. The client renders this
 * as `"<pathLabel> - <formatted vector>"` (or `"<pathLabel> - <fieldName>"`
 * when no snapshot was preserved). Formatting respects per-user unit settings,
 * which is why the raw snapshot ships rather than a pre-formatted string.
 */
export interface DuplicateAffectedField {
	/** Subform breadcrumb joined by ' / '. Empty string for root-level fields. */
	pathLabel: string;
	/** Field's display name. Used as the fallback label when `vector` is absent. */
	fieldName: string;
	/**
	 * Preserved manual snapshot for `span_demoted_no_partner` and
	 * `span_demoted_partner_failed`. Absent for `span_demoted_no_location`
	 * (the source had no complete snapshot to carry over).
	 */
	vector?: { distance: number; bearing: number; pitch: number };
}

/** Per-pole notification emitted by a duplicate task (failures + span demotions) */
export interface DuplicateNotification {
	kind: 'duplicate';
	sourceUuid: string;
	sourceTitle: string;
	code: DuplicateNotificationCode;
	message?: string;
	/** Vector fields demoted to manual. Only present for `span_demoted_*` codes. */
	affectedFields?: DuplicateAffectedField[];
}

/**
 * Umbrella notification type stored on `LongRunningTaskDocument.notifications`.
 *
 * Discriminated by `kind` — `DuplicateNotification` always sets `kind: 'duplicate'`,
 * `ExportNotification.kind` is optional ('export' or absent) so existing emitters
 * don't need to change. Read-side narrows via `n.kind === 'duplicate'`.
 */
export type Notification = ExportNotification | DuplicateNotification;

/** Union of all possible task result types */
export type TaskResult = ExportTaskResult | ImportTaskResult | DuplicateTaskResult;

export function isExportTaskResult(result: TaskResult): result is ExportTaskResult {
	return 'url' in result;
}

export function isImportTaskResult(result: TaskResult): result is ImportTaskResult {
	return 'jobResults' in result;
}

export function isDuplicateTaskResult(result: TaskResult): result is DuplicateTaskResult {
	return 'tagId' in result && 'collections' in result;
}

//@TODO: this needs to get uppdate to === export and the typing updated all through our codebase
export function isExportNotification(notification: Notification): notification is ExportNotification {
	return notification.kind !== 'duplicate';
}

export function isDuplicateNotification(notification: Notification): notification is DuplicateNotification {
	return notification.kind === 'duplicate';
}
